import { useState, useRef } from 'react';
import { useApp } from '../../context/AppContext';
import { uploadToServer } from '../../services/uploadService'; // ✅ USAR SEU UPLOAD SERVER

function Step2() {
  const { state, dispatch } = useApp();
  const { appData } = state;
  const [logo, setLogo] = useState(appData.logo || null);
  const [splash, setSplash] = useState(appData.splash || null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const logoRef = useRef(null);
  const splashRef = useRef(null);

  const handleUpload = async (file, type) => {
    setUploading(true);
    setUploadError(null);

    try {
      const folder = type === 'logo' ? 'logos' : 'splashs';
      const imageUrl = await uploadToServer(file, folder);

      if (type === 'logo') {
        setLogo(imageUrl);
        dispatch({
          type: 'UPDATE_APP_DATA',
          payload: { logo: imageUrl }
        });
      } else {
        setSplash(imageUrl);
        dispatch({
          type: 'UPDATE_APP_DATA',
          payload: { splash: imageUrl }
        });
      }

      return imageUrl;
    } catch (error) {
      console.error('Erro no upload:', error);
      setUploadError(error.message);
      throw error;
    } finally {
      setUploading(false);
    }
  };

  const handleLogoChange = async (e) => {
    if (e.target.files[0]) {
      const file = e.target.files[0];
      try {
        await handleUpload(file, 'logo');
      } catch (error) {
        alert(`Erro ao fazer upload da logo: ${error.message}`);
      }
    }
  };

  const handleSplashChange = async (e) => {
    if (e.target.files[0]) {
      const file = e.target.files[0];
      try {
        await handleUpload(file, 'splash');
      } catch (error) {
        alert(`Erro ao fazer upload da splash screen: ${error.message}`);
      }
    }
  };

  const handleDelete = (type) => {
    if (type === 'logo') {
      setLogo(null);
      dispatch({ type: 'UPDATE_APP_DATA', payload: { logo: null } });
    } else if (type === 'splash') {
      setSplash(null);
      dispatch({ type: 'UPDATE_APP_DATA', payload: { splash: null } });
    }
  };

  const triggerLogoInput = () => {
    logoRef.current?.click();
  };

  const triggerSplashInput = () => {
    splashRef.current?.click();
  };

  const handleNext = () => {
    dispatch({ type: 'UPDATE_PRICE', payload: { logo: 0 } });
    dispatch({ type: 'NEXT_STEP' });
  };

  const handleBack = () => {
    dispatch({ type: 'PREV_STEP' });
  };

  return (
    <div className="bg-white p-8 rounded-2xl shadow-xl">
      <h2 className="text-3xl font-bold mb-8 text-gray-900">Logo e Tela de Splash</h2>

      {uploadError && (
        <div className="mb-6 bg-red-100 border border-red-400 text-red-900 px-4 py-3 rounded-xl">
          <strong>Erro no upload:</strong> {uploadError}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
        {/* Logo */}
        <div>
          <label className="block text-sm font-semibold text-gray-900 mb-3">
            Logo do aplicativo *
          </label>
          <div
            className="mt-1 flex flex-col items-center justify-center px-6 pt-10 pb-8 border-2 border-dashed border-gray-200 rounded-xl cursor-pointer hover:border-blue-400 hover:bg-gray-50 transition-all duration-200"
            onClick={triggerLogoInput}
          >
            <input
              ref={logoRef}
              type="file"
              className="hidden"
              accept="image/*"
              onChange={handleLogoChange}
              disabled={uploading}
            />

            {uploading ? (
              <div className="text-center">
                <div className="w-10 h-10 border-4 border-gray-200 border-t-gray-900 animate-spin rounded-full mx-auto mb-4"></div>
                <p className="text-sm text-gray-600">Upload em progresso...</p>
              </div>
            ) : logo ? (
              <div className="text-center">
                <img
                  src={logo}
                  alt="Logo preview"
                  className="mx-auto h-32 w-32 object-contain mb-4 filter drop-shadow-sm"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
                {/* Lixeira para deletar */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete('logo');
                  }}
                  className="text-red-600 hover:text-red-800 text-sm mb-2 font-medium"
                >
                  🗑️ Deletar
                </button>
                <p className="text-sm text-blue-600 font-medium">Clique para alterar</p>
              </div>
            ) : (
              <div className="text-center">
                <svg className="mx-auto h-16 w-16 text-gray-300" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                  <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <div className="mt-4 flex text-sm text-gray-600">
                  <span className="relative rounded-md font-medium text-blue-600 hover:text-blue-500">
                    Enviar uma imagem
                  </span>
                </div>
                <p className="text-xs text-gray-500 mt-2">PNG, JPG, GIF até 10MB</p>
              </div>
            )}
          </div>
        </div>

        {/* Splash */}
        <div>
          <label className="block text-sm font-semibold text-gray-900 mb-3">
            Tela de Splash
          </label>
          <div
            className="mt-1 flex flex-col items-center justify-center px-6 pt-10 pb-8 border-2 border-dashed border-gray-200 rounded-xl cursor-pointer hover:border-blue-400 hover:bg-gray-50 transition-all duration-200"
            onClick={triggerSplashInput}
          >
            <input
              ref={splashRef}
              type="file"
              className="hidden"
              accept="image/*"
              onChange={handleSplashChange}
              disabled={uploading}
            />

            {uploading ? (
              <div className="text-center">
                <div className="w-10 h-10 border-4 border-gray-200 border-t-gray-900 animate-spin rounded-full mx-auto mb-4"></div>
                <p className="text-sm text-gray-600">Upload em progresso...</p>
              </div>
            ) : splash ? (
              <div className="text-center">
                <img
                  src={splash}
                  alt="Splash preview"
                  className="mx-auto h-32 w-32 object-contain mb-4 filter drop-shadow-sm"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
                {/* Lixeira para deletar */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete('splash');
                  }}
                  className="text-red-600 hover:text-red-800 text-sm mb-2 font-medium"
                >
                  🗑️ Deletar
                </button>
                <p className="text-sm text-blue-600 font-medium">Clique para alterar</p>
              </div>
            ) : (
              <div className="text-center">
                <svg className="mx-auto h-16 w-16 text-gray-300" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                  <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <div className="mt-4 flex text-sm text-gray-600">
                  <span className="relative rounded-md font-medium text-blue-600 hover:text-blue-500">
                    Enviar uma imagem
                  </span>
                </div>
                <p className="text-xs text-gray-500 mt-2">PNG, JPG, GIF até 10MB</p>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="mt-10 flex justify-between">
        <button
          onClick={handleBack}
          disabled={uploading}
          className="bg-gray-100 text-gray-900 px-8 py-3 rounded-xl hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-300 disabled:opacity-50 font-medium transition-all duration-200"
        >
          Voltar
        </button>
        <button
          onClick={handleNext}
          disabled={!logo || uploading} // <- Splash não é obrigatória
          className="bg-gray-900 text-white px-8 py-3 rounded-xl hover:bg-black focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed font-medium shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200"
        >
          {uploading ? 'Fazendo Upload...' : 'Próximo'}
        </button>
      </div>
    </div>
  );
}

export default Step2;
